import { Component, computed, inject, signal, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Marketplace, MatchType, Targeting } from "@front/m19-api-client";
import { SegmentEx } from "@front/m19-models";
import { SegmentService } from "@front/m19-services";
import {
  IButtonDirective,
  IFormFieldComponent,
  IInputDirective,
  ILabelComponent,
  MODAL_DATA,
  ModalOptions,
  ModalRef,
  ToasterService,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { Observable, of, switchMap } from "rxjs";
import { KeywordListManagerComponent } from "../../shared/keyword-list-manager/keyword-list-manager.component";

export interface KeywordSegmentModalData {
  segment?: SegmentEx;
  accountId?: string;
  marketplace?: Marketplace;
  initSegmentItems?: Targeting[];
  isReadOnly?: boolean;
}

@Component({
  selector: "app-keyword-segment-modal-v2",
  templateUrl: "./keyword-segment-modal.component.html",
  imports: [
    TranslocoDirective,
    FormsModule,
    IButtonDirective,
    KeywordListManagerComponent,
    IFormFieldComponent,
    ILabelComponent,
    IInputDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeywordSegmentModalComponent {
  static readonly defaultModalOptions: Omit<ModalOptions<unknown>, "data"> = { size: "lg", height: "fill" };

  public modalRef = inject(ModalRef<SegmentEx>);
  private segmentService = inject(SegmentService);
  private toastrService = inject(ToasterService);
  private translocoService = inject(TranslocoService);

  readonly MatchType = MatchType;
  private readonly modalData = inject<KeywordSegmentModalData>(MODAL_DATA);

  loading = signal(false);
  accountId: string | undefined;
  marketplace: Marketplace | undefined;
  name = signal("");
  existingSegment: SegmentEx | undefined;
  segmentTargetings = signal<Targeting[]>([]);
  isReadOnly = false;
  saveDisabled = computed(() => this.segmentTargetings()?.length === 0);

  constructor() {
    this.name.set(this.modalData.segment?.name ?? "");
    this.accountId = this.modalData.accountId;
    this.marketplace = this.modalData.marketplace;
    this.existingSegment = this.modalData.segment;
    this.segmentTargetings.set(this.modalData.initSegmentItems ?? []);
    this.isReadOnly = !!this.modalData.isReadOnly;
  }

  onKeywordsChanged(keywords: Targeting[]) {
    this.segmentTargetings.set(keywords);
  }

  saveSegment() {
    if (this.existingSegment) {
      this.updateSegment();
    } else {
      this.createSegment();
    }
  }

  updateSegment() {
    this.loading.set(true);
    this.updateSegmentName(this.name()!)
      .pipe(
        switchMap((_) => this.deleteKeywordsFromSegment(this.existingSegment!, this.segmentTargetings()!)),
        switchMap((_) => this.addKeywordsToSegment(this.existingSegment!, this.segmentTargetings()!)),
      )
      .subscribe({
        next: (s) => {
          this.toastrService.success(this.translocoService.translate("keyword-segment-modal.segment_updated"));
          this.modalRef.close(s);
        },
        error: (err) => {
          this.toastrService.error(this.translocoService.translate("keyword-segment-modal.segment_update_error"), err);
          this.loading.set(false);
        },
      });
  }

  private deleteKeywordsFromSegment(segment: SegmentEx, keywords: Targeting[]): Observable<SegmentEx> {
    const toDelete = segment.items.filter(
      (k) => !keywords.find((i) => i.targetingValue === k.targetingValue && i.matchType === k.matchType),
    );

    if (toDelete.length === 0) {
      return of(segment);
    }
    return this.segmentService.deleteItemsFromSegment(this.accountId!, this.marketplace!, segment.segmentId, toDelete);
  }

  private addKeywordsToSegment(segment: SegmentEx, keywords: Targeting[]): Observable<SegmentEx> {
    const toAdd = keywords.filter(
      (k) => !segment.items.find((i) => i.targetingValue === k.targetingValue && i.matchType === k.matchType),
    );

    if (toAdd.length === 0) {
      return of(segment);
    }
    return this.segmentService.addKeywordsToSegment(this.accountId!, this.marketplace!, segment.segmentId, toAdd);
  }

  private updateSegmentName(newName: string): Observable<SegmentEx> {
    return this.name() !== this.existingSegment?.name
      ? this.segmentService.updateSegmentName(
          this.accountId!,
          this.marketplace!,
          this.existingSegment!.segmentId,
          newName,
        )
      : of(this.existingSegment!);
  }

  private createSegment() {
    this.loading.set(true);
    this.segmentService
      .createSegment(this.accountId!, this.marketplace!, this.name())
      .pipe(
        switchMap((segment: SegmentEx) => {
          return this.segmentService.addKeywordsToSegment(
            this.accountId!,
            this.marketplace!,
            segment.segmentId,
            this.segmentTargetings()!,
          );
        }),
      )
      .subscribe({
        next: (s) => {
          this.toastrService.success(
            this.translocoService.translate("keyword-segment-modal.segment_s_name_created", [s.name]),
            this.translocoService.translate("keyword-segment-modal.segment_created"),
          );
          this.modalRef.close(s);
          this.loading.set(false);
        },
        error: (err) => {
          this.toastrService.error(
            this.translocoService.translate("keyword-segment-modal.error_creating_segment") + ": " + err,
            this.translocoService.translate("keyword-segment-modal.segment_creation_error"),
          );
          this.loading.set(false);
        },
      });
  }
}
